import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Skeleton,
  LinearProgress,
  IconButton,
  Tooltip as MuiTooltip,
  alpha,
} from "@mui/material";
import {
  Refresh,
  Download,
  WarningAmber,
  Public,
  FilterList,
  CheckCircle,
  ErrorOutline,
  TrendingUp,
  TrendingDown,
  PersonOutline,
  AirplanemodeActive,
  LocationOn,
  CalendarToday,
  SyncAlt,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Circle,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart as ReBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
  ReferenceLine,
} from "recharts";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { reportsAPI, travelerScreeningAPI } from "../services/api";
import GeoDashboardMap from "../components/Dashboard/GeoDashboardMap";
import PortEntryMap from "../components/Dashboard/PortEntryMap";
import { formatKenyaYmd, formatKenyaClock, parseYmdAsUtcNoon } from "../utils/dateUtils";
import { formatPortName } from "../utils/formatPortName";
import { F, useDashTokens, riskClr as riskClrTok, riskBgClr as riskBgClrTok, DashTokens } from "../theme/dashTokens";

/* ═══════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — per-mode tokens live in theme/dashTokens.ts;
   components read them via useDashTokens()
══════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════
   DATA CONFIG
══════════════════════════════════════════════════════════════════ */
const KPI_CONFIG = [
  { label: "Total Screenings",   key: "total_screenings", icon: PersonOutline,      colorKey: "blue"  as keyof DashTokens, cumulative_key: "total_screenings_cumulative" },
  { label: "Cleared / Passed",   key: "cleared",          icon: CheckCircle,        colorKey: "green" as keyof DashTokens, cumulative_key: "cleared_cumulative",   pct_key: "cleared_pct" },
  { label: "High-Risk Alerts",   key: "high_risk",        icon: WarningAmber,       colorKey: "red"   as keyof DashTokens, cumulative_key: "high_risk_cumulative", pct_key: "high_risk_pct" },
  { label: "Point of Entry",     key: "active_ports",     icon: AirplanemodeActive, colorKey: "muted" as keyof DashTokens },
];

const numFmt = (v: number | undefined | null) => (v ?? 0).toLocaleString();


/* ═══════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════════════════ */

const LivePulse: React.FC<{ color?: string }> = ({ color }) => {
  const { C } = useDashTokens();
  const dot = color ?? C.green;
  return (
    <Box sx={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
      <Box sx={{
        width: 8, height: 8, borderRadius: "50%", background: dot, position: "absolute",
        "@keyframes pulse": {
          "0%":   { boxShadow: `0 0 0 0 ${dot}66` },
          "70%":  { boxShadow: `0 0 0 6px ${dot}00` },
          "100%": { boxShadow: `0 0 0 0 ${dot}00`  },
        },
        animation: "pulse 2s infinite",
      }} />
    </Box>
  );
};

const AnimNumber: React.FC<{ value: number; duration?: number }> = ({ value, duration = 800 }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = value;
    const startTime = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (t < 1) requestAnimationFrame(tick);
      else prev.current = end;
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{numFmt(display)}</>;
};

const ChartTip = ({ active, payload, label }: any) => {
  const { C, S } = useDashTokens();
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      background: C.headerBg, color: C.onDark, px: 2, py: 1.5, borderRadius: "10px",
      boxShadow: S.lg, border: "1px solid rgba(255,255,255,0.08)", fontFamily: F.mono,
    }}>
      <Typography sx={{ fontSize: 10, color: C.onDarkMuted, mb: 0.8, fontFamily: F.mono }}>{label}</Typography>
      {payload.map((p: any) => (
        <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: p.color }} />
          <Typography sx={{ fontSize: 11, fontFamily: F.mono, color: C.onDark }}>
            {p.name}: <strong>{numFmt(p.value)}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const SectionTitle: React.FC<{ title: string; sub?: string; right?: React.ReactNode }> = ({ title, sub, right }) => {
  const { C } = useDashTokens();
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
      <Box>
        <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 14, color: C.ink, lineHeight: 1.3 }}>{title}</Typography>
        {sub && <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.muted, mt: 0.3 }}>{sub}</Typography>}
      </Box>
      {right}
    </Box>
  );
};

const RiskBadge: React.FC<{ label: string; displayLabel?: string }> = ({ label, displayLabel }) => {
  const { C } = useDashTokens();
  return (
    <Chip label={displayLabel ?? label} size="small" sx={{
      bgcolor: riskBgClrTok(C, label), color: riskClrTok(C, label),
      fontFamily: F.mono, fontSize: 10, fontWeight: 700,
      height: 20, borderRadius: "5px",
      border: `1px solid ${riskClrTok(C, label)}30`,
    }} />
  );
};

// The backend only ever persists risk_level as "high_risk" or "cleared"
// (see assess_risk / assess_ebola_risk in traveler_screening_service.py) —
// there is no real "moderate" tier, so the UI must only offer these two.
const RISK_LEVEL_LABELS: Record<string, string> = {
  high_risk: "High Risk",
  cleared: "Low Risk",
};

// Maps the dropdown's display label back to the raw value stored in the DB,
// since the API filters with an exact (lower-cased) match against risk_level.
const RISK_LEVEL_TO_API: Record<string, string> = {
  "High Risk": "high_risk",
  "Low Risk": "cleared",
};

const ORIGIN_PALETTE_KEYS: (keyof DashTokens)[] = ["blue", "teal", "purple", "amber", "red", "green", "orange"];

const HighRiskCorridorList: React.FC<{
  corridors: { origin: string; entry_city: string; entry_point_title?: string | null; count: number }[];
  totalHighRisk: number;
  loading: boolean;
}> = ({ corridors, totalHighRisk, loading }) => {
  const { C } = useDashTokens();

  const originColorMap = useMemo(() => {
    const uniqueOrigins = Array.from(new Set(corridors.map((c) => c.origin)));
    const map: Record<string, string> = {};
    uniqueOrigins.forEach((name, i) => { map[name] = C[ORIGIN_PALETTE_KEYS[i % ORIGIN_PALETTE_KEYS.length]] as string; });
    return map;
  }, [corridors, C]);

  if (loading) return <Skeleton variant="rounded" height={320} />;
  if (!corridors.length) {
    return (
      <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.muted, py: 6, textAlign: "center" }}>
        No high-risk travelers in the selected period
      </Typography>
    );
  }

  const maxCount = Math.max(...corridors.map((c) => c.count), 1);
  const originCount = Object.keys(originColorMap).length;

  return (
    <Box>
      <Typography sx={{ fontFamily: F.mono, fontSize: 10.5, color: C.muted, mb: 1.6 }}>
        {totalHighRisk.toLocaleString()} high-risk traveler{totalHighRisk === 1 ? "" : "s"} across {originCount} origin{originCount === 1 ? "" : "s"}
      </Typography>
      <Box sx={{ display: "grid", gap: 1.1, maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
        {corridors.map((c, idx) => {
          const pct = Math.round((c.count / maxCount) * 100);
          const color = originColorMap[c.origin];
          return (
            <Box key={`${c.origin}__${c.entry_city}__${idx}`}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: F.sans, fontSize: 12, fontWeight: 600, color: C.ink, whiteSpace: "nowrap" }}>
                    {formatPortName(c.origin)}
                  </Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.muted, flexShrink: 0 }}>→</Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.entry_point_title || formatPortName(c.entry_city)}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.sub, fontWeight: 700, flexShrink: 0 }}>
                  {c.count.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 3, background: C.divider, overflow: "hidden" }}>
                <Box sx={{ height: "100%", borderRadius: 3, background: color, width: `${pct}%`, transition: "width 1s cubic-bezier(.4,0,.2,1)" }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const HEAT_ALPHA_STEPS = ["14", "2e", "52", "7a", "b0"];
type HeatBin = { min: number; max: number; alpha: string };
const buildHeatQuantileScale = (values: number[]): HeatBin[] => {
  const nz = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (!nz.length) return [];
  const thresholds = HEAT_ALPHA_STEPS.map((_, i) => {
    const rank = Math.ceil(((i + 1) / HEAT_ALPHA_STEPS.length) * nz.length) - 1;
    return nz[Math.max(0, Math.min(rank, nz.length - 1))];
  });
  const scale: HeatBin[] = [];
  let min = 0;
  thresholds.forEach((max, i) => {
    if (max < min) return;
    scale.push({ min, max, alpha: HEAT_ALPHA_STEPS[i] });
    min = max + 1;
  });
  return scale;
};
const getHeatAlpha = (value: number, scale: HeatBin[]): string =>
  (scale.find((b) => value >= b.min && value <= b.max) || scale[scale.length - 1])?.alpha || "00";

const ActivityHeatmapGrid: React.FC<{
  days: string[];
  hours: number[];
  matrix: number[][];
  rowTotals: number[];
  colTotals: number[];
  grandTotal: number;
  loading: boolean;
}> = ({ days, hours, matrix, rowTotals, colTotals, grandTotal, loading }) => {
  const { C } = useDashTokens();
  const scale = useMemo(() => buildHeatQuantileScale(matrix.flat()), [matrix]);

  if (loading) return <Skeleton variant="rounded" height={320} />;
  if (!grandTotal) {
    return (
      <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.muted, py: 6, textAlign: "center" }}>
        No screening activity for the selected filters
      </Typography>
    );
  }

  const cols = `86px repeat(${hours.length}, 1fr) 56px`;

  return (
    <Box sx={{ opacity: 0, animation: "activityHeatIn .4s ease both", "@keyframes activityHeatIn": { from: { opacity: 0 }, to: { opacity: 1 } } }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: cols, minWidth: 900, gap: "2px" }}>
          <Box />
          {hours.map((h) => (
            <Typography key={h} sx={{ fontFamily: F.mono, fontSize: 9, color: C.muted, textAlign: "center", pb: 0.4 }}>{h}</Typography>
          ))}
          <Typography sx={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, color: C.muted, textAlign: "center", pb: 0.4 }}>Total</Typography>

          {days.map((day, di) => (
            <React.Fragment key={day}>
              <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.ink, fontWeight: 600, display: "flex", alignItems: "center" }}>
                {day}
              </Typography>
              {hours.map((h) => {
                const v = matrix[di]?.[h] || 0;
                const alphaHex = v > 0 ? getHeatAlpha(v, scale) : "00";
                return (
                  <Box
                    key={h}
                    title={`${day} ${h}:00 — ${v.toLocaleString()} screened`}
                    sx={{
                      aspectRatio: "1",
                      borderRadius: "3px",
                      background: v > 0 ? `${C.blue}${alphaHex}` : C.divider,
                      transition: "transform .12s ease, box-shadow .12s ease",
                      ...(v > 0 ? { "&:hover": { transform: "scale(1.2)", boxShadow: `0 0 0 1px ${C.blue}66`, zIndex: 2, position: "relative" } } : {}),
                    }}
                  />
                );
              })}
              <Typography sx={{ fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: C.sub, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {rowTotals[di]?.toLocaleString() ?? 0}
              </Typography>
            </React.Fragment>
          ))}

          <Typography sx={{ fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: C.sub }}>Total</Typography>
          {hours.map((h) => (
            <Typography key={h} sx={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, color: C.sub, textAlign: "center" }}>
              {colTotals[h]?.toLocaleString() ?? 0}
            </Typography>
          ))}
          <Typography sx={{ fontFamily: F.mono, fontSize: 10, fontWeight: 800, color: C.ink, textAlign: "center" }}>
            {grandTotal.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
        <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>Low</Typography>
        <Box sx={{ flex: 1, maxWidth: 140, height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${C.blue}14, ${C.blue}b0)` }} />
        <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>High</Typography>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const DashboardPage: React.FC = () => {
  const { C, S, card, glow, mode } = useDashTokens();
  const [myEntryPoints, setMyEntryPoints] = useState<{ restricted: boolean; ports: { code: string; title: string }[] } | null>(null);
  const [data,         setData        ] = useState<any>(null);
  const [loading,      setLoading     ] = useState(true);
  const [error,        setError       ] = useState<string | null>(null);
  // Date filters use Kenya calendar days (not the browser's IST "today")
  const [startDate,    setStartDate   ] = useState<Date>(() => parseYmdAsUtcNoon(formatKenyaYmd()));
  const [endDate,      setEndDate     ] = useState<Date>(() => parseYmdAsUtcNoon(formatKenyaYmd()));
  const [selectedPort, setSelectedPort] = useState("ALL");
  const [selectedRisk, setSelectedRisk] = useState("ALL");
  const [kenyaClock,   setKenyaClock  ] = useState(() => formatKenyaClock());

  const RISK_COLORS: Record<string, string> = useMemo(() => ({
    "High Risk": C.red, "Low Risk": C.green,
  }), [C]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Use the calendar day shown in the picker (not a TZ reinterpretation of the instant).
      // Initial "today" is seeded from Kenya; thereafter the picked day is authoritative.
      const result = await reportsAPI.getSituationRoom({
        // Use Kenya calendar YMD — never date-fns format() (that uses browser IST/local TZ).
        start_date: formatKenyaYmd(startDate),
        end_date:   formatKenyaYmd(endDate),
        port:       selectedPort !== "ALL" ? selectedPort : undefined,
        risk_level: selectedRisk !== "ALL" ? RISK_LEVEL_TO_API[selectedRisk] : undefined,
      });
      setData(result);
    } catch (e: any) {
      setError(e?.message || "Unable to load data.");
    } finally { setLoading(false); }
  }, [startDate, endDate, selectedPort, selectedRisk]);

  useEffect(() => {
    fetchData();
    const id = window.setInterval(fetchData, 30_000);
    return () => window.clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    const id = window.setInterval(() => setKenyaClock(formatKenyaClock()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    travelerScreeningAPI.getMyEntryPoints()
      .then(setMyEntryPoints)
      .catch(() => setMyEntryPoints(null));
  }, []);

  const dateParams = useMemo(() => ({
    start_date: formatKenyaYmd(startDate),
    end_date:   formatKenyaYmd(endDate),
  }), [startDate, endDate]);

  const [corridorData, setCorridorData] = useState<any>(null);
  const [corridorLoading, setCorridorLoading] = useState(true);
  const fetchCorridor = useCallback(async () => {
    setCorridorLoading(true);
    try {
      const result = await reportsAPI.getHighRiskCorridor({
        ...dateParams,
        port: selectedPort !== "ALL" ? selectedPort : undefined,
      });
      setCorridorData(result);
    } catch {
      setCorridorData(null);
    } finally { setCorridorLoading(false); }
  }, [dateParams, selectedPort]);

  useEffect(() => {
    fetchCorridor();
    const id = window.setInterval(fetchCorridor, 30_000);
    return () => window.clearInterval(id);
  }, [fetchCorridor]);

  const [activityHeatmapData, setActivityHeatmapData] = useState<any>(null);
  const [activityHeatmapLoading, setActivityHeatmapLoading] = useState(true);
  const fetchActivityHeatmap = useCallback(async () => {
    setActivityHeatmapLoading(true);
    try {
      const result = await reportsAPI.getScreeningActivityHeatmap({
        ...dateParams,
        port: selectedPort !== "ALL" ? selectedPort : undefined,
      });
      setActivityHeatmapData(result);
    } catch {
      setActivityHeatmapData(null);
    } finally { setActivityHeatmapLoading(false); }
  }, [dateParams, selectedPort]);

  useEffect(() => {
    fetchActivityHeatmap();
    const id = window.setInterval(fetchActivityHeatmap, 30_000);
    return () => window.clearInterval(id);
  }, [fetchActivityHeatmap]);

  const [feedback, setFeedback] = useState<any>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [followup, setFollowup] = useState<any>(null);
  const [followupLoading, setFollowupLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const dashboardExportRef = useRef<HTMLDivElement | null>(null);

  const fetchFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    try {
      const result = await reportsAPI.getFeedback({ ...dateParams });
      setFeedback(result);
    } catch {
      setFeedback(null);
    } finally { setFeedbackLoading(false); }
  }, [dateParams]);

  const fetchFollowup = useCallback(async () => {
    setFollowupLoading(true);
    try {
      const result = await reportsAPI.getFollowupInsights({ ...dateParams });
      setFollowup(result);
    } catch {
      setFollowup(null);
    } finally { setFollowupLoading(false); }
  }, [dateParams]);

  useEffect(() => {
    fetchFeedback();
    const id = window.setInterval(fetchFeedback, 30_000);
    return () => window.clearInterval(id);
  }, [fetchFeedback]);

  useEffect(() => {
    fetchFollowup();
    const id = window.setInterval(fetchFollowup, 30_000);
    return () => window.clearInterval(id);
  }, [fetchFollowup]);

  const exportData = async () => {
    const el = dashboardExportRef.current;
    if (!el || exportingPdf) return;
    setExportingPdf(true);
    try {
      const canvas = await html2canvas(el, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: mode === "dark" ? "#0b1220" : "#f4f7f9",
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 18;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const imgW = usableW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = margin;
      pdf.addImage(imgData, "JPEG", margin, position, imgW, imgH);
      heightLeft -= usableH;
      while (heightLeft > 0) {
        position = margin - (imgH - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", margin, position, imgW, imgH);
        heightLeft -= usableH;
      }
      pdf.save(`ebola_dashboard_${formatKenyaYmd()}_${kenyaClock.time.replace(":", "")}.pdf`);
    } catch (e) {
      console.error("Dashboard PDF export failed", e);
    } finally {
      setExportingPdf(false);
    }
  };

  const summary = data?.summary || {};

  const tRisk = useCallback((raw: string = "") => {
    const l = raw.toLowerCase();
    if (l.includes("high")) return "High Risk";
    if (l.includes("mod"))  return "Moderate";
    if (l.includes("low"))  return "Low Risk";
    return raw;
  }, []);

  // Prefer the resolved entry_points.title over re-formatting a code/title
  // string — formatPortName() title-cases unknown words (e.g. "OSBP" -> "Osbp"),
  // which is only correct for raw codes, not titles that already came from the DB.
  const displayPort = useCallback((p: any) => p?.port_title || formatPortName(p?.port_name || p?.port), []);

  // Sourced from /my-entry-points (stable, unfiltered, access-scoped list) rather
  // than data.ports — data.ports reflects whatever port filter is currently
  // applied, so building options from it made every other port disappear
  // from the dropdown as soon as one was selected. Values are entry_city
  // codes (what the backend filters on), not titles.
  const portOptions = useMemo(() => {
    if (myEntryPoints?.ports?.length) {
      return [
        { code: "ALL", title: "All Ports" },
        ...myEntryPoints.ports.map((p) => ({ code: p.code, title: p.title })),
      ];
    }
    const seen = new Map<string, string>();
    (data?.ports || []).forEach((p: any) => {
      const code = p?.port;
      if (code && !seen.has(code)) seen.set(code, p?.port_title || p?.port_name || code);
    });
    return [{ code: "ALL", title: "All Ports" }, ...Array.from(seen, ([code, title]) => ({ code, title }))];
  }, [myEntryPoints, data]);

  const trendData = useMemo(() =>
    (data?.screening_trend || []).map((i: any) => ({
      date: i.date,
      screenings:       i.screenings       || 0,
      high_risk_alerts: i.high_risk_alerts || 0,
      cleared:          i.cleared          || 0,
    })), [data]);

  const riskChartData = useMemo(() =>
    (data?.risk_distribution || []).map((i: any) => {
      const name = RISK_LEVEL_LABELS[(i.risk_level || "").toLowerCase()] || i.risk_level;
      return { name, value: i.count, color: RISK_COLORS[name] || C.gray };
    }), [data, RISK_COLORS, C.gray]);

  const outcomeByPort = useMemo(() =>
    (data?.ports || []).slice(0, 6).map((p: any) => ({
      port:      displayPort(p).split(" ").slice(0, 2).join(" "),
      cleared:   p.cleared   || 0,
      high_risk: p.high_risk || 0,
      follow_up: p.suspected || 0,
    })), [data, displayPort]);

  // Merge names that differ only by case/spacing (e.g. "kenya" / "Kenya"), then take the top 6.
  const countryData = useMemo(() => {
    const titleCase = (s: string) =>
      s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\b(Of|And|The)\b/g, (w, _m, i) => (i === 0 ? w : w.toLowerCase()));
    const merged = new Map<string, { country: string; count: number }>();
    (data?.country_distribution || []).forEach((item: any) => {
      const raw = String(item.country || "").trim().replace(/\s+/g, " ");
      const key = raw.toLowerCase() || "unknown";
      const entry = merged.get(key) || { country: raw ? titleCase(raw) : "Unknown", count: 0 };
      entry.count += Number(item.count) || 0;
      merged.set(key, entry);
    });
    return Array.from(merged.values()).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [data]);
  // The 11 Ebola screening-form symptoms (list defined in backend reports.py).
  const symptomData = useMemo(() => (data?.symptom_distribution || []).slice(0, 11), [data]);

  const feedbackSummary = feedback?.summary || {};
  const satisfaction = feedbackSummary.overall_satisfaction ?? feedbackSummary.net_score ?? 0;
  const satColor = satisfaction >= 60 ? C.green : satisfaction >= 40 ? C.amber : C.red;
  const satLabel = satisfaction >= 60 ? "Mostly Satisfied" : satisfaction >= 40 ? "Mixed" : "Needs Attention";

  const mapPoints = useMemo(
    () =>
      (data?.country_distribution || []).map((item: any) => ({
        country: item.country,
        count: item.count,
        high_risk_count: item.high_risk_count || 0,
        moderate_risk_count: item.moderate_risk_count || 0,
        risk: item.high_risk_count > 0
          ? "High Risk"
          : item.moderate_risk_count > 0
          ? "Moderate Risk"
          : "Low Risk",
      })),
    [data]
  );

  const portMapPoints = useMemo(
    () =>
      (data?.ports || []).map((p: any) => ({
        port: displayPort(p),
        entry_type: p.entry_type,
        screened: p.screened,
        high_risk: p.high_risk,
        latitude: p.latitude,
        longitude: p.longitude,
      })),
    [data, displayPort]
  );

  const trendDelta = useMemo(() => {
    if (trendData.length < 2) return null;
    const last = trendData[trendData.length - 1].screenings;
    const prev = trendData[trendData.length - 2].screenings;
    if (!prev) return null;
    return Math.round(((last - prev) / prev) * 1000) / 10;
  }, [trendData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: "100vh", background: `linear-gradient(180deg, ${C.pageBgTop} 0%, ${C.pageBg} 320px)`, fontFamily: F.sans }}>

        {/* ══ HEADER ══════════════════════════════════════════════ */}
        <Box sx={{
          background: `linear-gradient(135deg, ${C.headerBg} 0%, ${C.headerBg2} 55%, ${C.headerBg} 100%)`,
          borderBottom: `1px solid ${C.headerBorder}`,
          position: "sticky", top: 0, zIndex: 100,
          boxShadow: "0 2px 24px rgba(0,0,0,0.3)",
        }}>
          <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 0, display: "flex", alignItems: "stretch", minHeight: 64 }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 3, borderRight: `1px solid ${C.headerBorder}`, mr: 3 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                  <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 13, color: C.onDark, lineHeight: 1.2 }}>UHAI</Typography>
                  <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }} aria-label="Kenya flag" role="img">🇰🇪</Box>
                </Box>
                <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.onDarkMuted }}>Kinga · Fuatilia · Okoa</Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 15, color: C.onDark, lineHeight: 1.2 }}>
                Ebola Situation Room
              </Typography>
              <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDarkMuted, mt: 0.2 }}>
                Real-time port of entry surveillance and screening overview
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {[
                { icon: <Public sx={{ fontSize: 15, color: C.teal }} />, line1: "Kenya MOH", line2: "Ministry of Health" },
                {
                  icon: (
                    <Box
                      component="img"
                      src="/logo/NPHI.jpeg"
                      alt="KNPHI"
                      sx={{
                        height: 22, width: "auto", maxWidth: 70, objectFit: "contain",
                        background: "rgba(255,255,255,0.92)", borderRadius: "4px", px: 0.6, py: 0.25,
                      }}
                    />
                  ),
                  line1: "KNPHI", line2: "Kenya National Public Health Institute",
                },
              ].map((b, i) => (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.5, py: 0.8,
                  border: `1px solid ${C.headerBorder}`,
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(6px)",
                  transition: "background .2s ease, border-color .2s ease",
                  "&:hover": { background: "rgba(255,255,255,0.08)", borderColor: "rgba(13,148,136,0.4)" },
                }}>
                  {b.icon}
                  <Box>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.onDark, lineHeight: 1.2 }}>{b.line1}</Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.onDarkMuted, lineHeight: 1.2 }}>{b.line2}</Typography>
                  </Box>
                </Box>
              ))}

              <Box sx={{ px: 1.5, py: 0.8, border: `1px solid ${C.headerBorder}`, borderRadius: "8px", background: "rgba(255,255,255,0.04)" }}>
                <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.onDark, fontWeight: 600 }}>
                  {kenyaClock.date}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.2 }}>
                  <LivePulse />
                  <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.onDarkMuted }}>
                    {kenyaClock.time} EAT &nbsp;·&nbsp; Kenya Time
                  </Typography>
                </Box>
              </Box>

            </Box>
          </Box>
        </Box>

        {/* ══ PAGE CONTENT ════════════════════════════════════════ */}
        <Box
          ref={dashboardExportRef}
          sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}
        >

          {/* ── FILTER BAR ─────────────────────────────────────── */}
          <Box sx={{ ...card(), px: 2.5, py: 1.8, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterList sx={{ fontSize: 15, color: C.muted }} />
              <Typography sx={{ fontFamily: F.sans, fontSize: 12, fontWeight: 600, color: C.sub }}>Filters:</Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <DatePicker
                label="From Date"
                format="dd/MM/yyyy"
                value={startDate}
                maxDate={endDate}
                onChange={(d) => d && setStartDate(d)}
                slotProps={{ textField: { size: "small", sx: { "& .MuiInputBase-root": { fontFamily: F.mono, fontSize: 12, borderRadius: "8px" } } } }}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <DatePicker
                label="To Date"
                format="dd/MM/yyyy"
                value={endDate}
                minDate={startDate}
                maxDate={new Date()}
                onChange={(d) => d && setEndDate(d)}
                slotProps={{ textField: { size: "small", sx: { "& .MuiInputBase-root": { fontFamily: F.mono, fontSize: 12, borderRadius: "8px" } } } }}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: F.mono, fontSize: 12 }}>{"Port of Entry"}</InputLabel>
              <Select value={selectedPort} label={"Port of Entry"} onChange={(e) => setSelectedPort(e.target.value)} sx={{ fontFamily: F.mono, fontSize: 12, borderRadius: "8px" }}>
                {portOptions.map((p) => (
                  <MenuItem key={p.code} value={p.code} sx={{ fontFamily: F.mono, fontSize: 12 }}>{p.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {myEntryPoints?.restricted && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.6, borderRadius: "8px", background: C.tealLt }}>
                <Typography sx={{ fontFamily: F.mono, fontSize: 10.5, color: C.teal, fontWeight: 700 }}>
                  {myEntryPoints.ports.length
                    ? `Scoped to: ${myEntryPoints.ports.map((p) => p.title).join(", ")}`
                    : "No ports assigned yet — contact an admin"}
                </Typography>
              </Box>
            )}

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: F.mono, fontSize: 12 }}>Risk Level</InputLabel>
              <Select value={selectedRisk} label="Risk Level" onChange={(e) => setSelectedRisk(e.target.value)} sx={{ fontFamily: F.mono, fontSize: 12, borderRadius: "8px" }}>
                {["ALL", "High Risk", "Low Risk"].map((r) => (
                  <MenuItem key={r} value={r} sx={{ fontFamily: F.mono, fontSize: 12 }}>
                    {r === "ALL" ? "All Risk Levels" : r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              <Button variant="outlined" size="small" onClick={fetchData} startIcon={<Refresh sx={{ fontSize: 14 }} />}
                sx={{ fontFamily: F.sans, fontSize: 12, textTransform: "none", borderColor: C.cardBorder, color: C.sub, borderRadius: "8px", "&:hover": { borderColor: C.teal, color: C.teal, background: C.tealLt } }}>
                Reset Filters
              </Button>
              <Button variant="contained" size="small" onClick={exportData} disabled={exportingPdf || !data}
                startIcon={<Download sx={{ fontSize: 14 }} />}
                sx={{ fontFamily: F.sans, fontSize: 12, textTransform: "none", background: `linear-gradient(135deg, ${C.teal}, ${C.tealDk})`, borderRadius: "8px", boxShadow: "0 2px 8px rgba(13,148,136,0.35)", "&:hover": { background: `linear-gradient(135deg, ${C.tealDk}, #085e58)` } }}>
                {exportingPdf ? "Exporting…" : "Export Report"}
              </Button>
            </Box>
          </Box>

          {/* ── KPI CARDS ───────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 2 }}>
            {KPI_CONFIG.map((cfg) => {
              const IconComp = cfg.icon;
              const periodVal     = summary[cfg.key] ?? 0;
              const cumulativeVal = (cfg as any).cumulative_key ? (summary[(cfg as any).cumulative_key] ?? 0) : null;
              const pct           = (cfg as any).pct_key ? summary[(cfg as any).pct_key] : null;
              const isPortCard    = cfg.key === "active_ports";
              const color         = String(C[cfg.colorKey] ?? C.muted);

              return (
                <Box key={cfg.key} sx={{
                  ...card(),
                  borderLeft: `3px solid ${color}`,
                  p: 2.2,
                  "&:hover": { transform: "translateY(-3px)", boxShadow: S.xl },
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: isPortCard ? C.divider : `${color}1a`,
                      display: "grid", placeItems: "center", color, flexShrink: 0,
                    }}>
                      <IconComp sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontFamily: F.sans, fontWeight: 700, fontSize: 13, color: C.ink }}>{cfg.label}</Typography>
                  </Box>

                  {isPortCard ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.sub }}>Active</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 20, fontWeight: 800, color: C.ink, textShadow: glow(color) }}>
                          {loading ? <Skeleton width={40} /> : <AnimNumber value={periodVal} />}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <CalendarToday sx={{ fontSize: 12, color: C.muted }} />
                          <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.sub }}>Cumulative to date</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 18, fontWeight: 700, color: C.ink }}>
                          {loading ? <Skeleton width={40} /> : <AnimNumber value={cumulativeVal ?? 0} />}
                        </Typography>
                      </Box>

                      <Box sx={{ background: `${color}12`, borderRadius: "12px", p: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.8 }}>
                          <FilterList sx={{ fontSize: 12, color }} />
                          <Typography sx={{ fontFamily: F.mono, fontSize: 10.5, fontWeight: 600, color }}>
                            {format(startDate, "MMM d")} – {format(endDate, "MMM d, yyyy")}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 24, fontWeight: 800, color, lineHeight: 1, textShadow: glow(color) }}>
                          {loading ? <Skeleton width={50} /> : <AnimNumber value={periodVal} />}
                        </Typography>
                        {pct != null && (
                          <Typography sx={{ fontFamily: F.mono, fontSize: 10, color, opacity: 0.8, mt: 0.4 }}>
                            {pct}% of period screenings
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* ── MAPS ROW ────────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>

            <Box sx={{ ...card(), overflow: "hidden", "&:hover": { boxShadow: S.md } }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}` }}>
                <SectionTitle title="Screenings by Country of Origin" />
              </Box>
              <Box sx={{ height: 480 }}>
                <GeoDashboardMap
                  mode={mode}
                  points={mapPoints}
                  colors={{
                    tooltipBg: C.headerBg,
                    tooltipText: C.onDark,
                    tooltipTextMuted: C.onDarkMuted,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ ...card(), overflow: "hidden", "&:hover": { boxShadow: S.md } }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}` }}>
                <SectionTitle title="Entry Points by Type" sub="Air · Sea · Land" />
              </Box>
              <Box sx={{ height: 480 }}>
                <PortEntryMap
                  mode={mode}
                  ports={portMapPoints}
                  colors={{
                    air: C.gray,
                    sea: C.blue,
                    land: C.amber,
                    tooltipBg: C.headerBg,
                    tooltipText: C.onDark,
                    tooltipTextMuted: C.onDarkMuted,
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* ── PORT TABLE (full width) ─────────────────────────── */}
          <Box sx={{ ...card(), display: "flex", flexDirection: "column", overflow: "hidden", "&:hover": { boxShadow: S.md } }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.cardBorder}` }}>
              <SectionTitle title="High-Risk Alerts by Port of Entry" />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "32px 1fr 100px 90px 100px 110px", px: 2.5, py: 1.3, background: C.pageBg, borderBottom: `1px solid ${C.cardBorder}`, gap: 1.5 }}>
              {[
                "#",
                "PORT OF ENTRY",
                "SCREENED",
                "CLEARED",
                "HIGH-RISK",
                "STATUS",
              ].map((h) => (
                <Typography key={h} sx={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.6 }}>{h}</Typography>
              ))}
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 360 }}>
              {loading && !data
                ? Array.from({ length: 7 }).map((_, i) => (
                    <Box key={i} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${C.divider}` }}>
                      <Skeleton height={20} />
                    </Box>
                  ))
                : (data?.ports || []).map((port: any, idx: number) => {
                    const name = displayPort(port);
                    const st   = port.status    || "Low Risk";
                    return (
                      <Box key={name + idx} sx={{
                        display: "grid", gridTemplateColumns: "32px 1fr 100px 90px 100px 110px",
                        px: 2.5, py: 1.6, borderBottom: `1px solid ${C.divider}`,
                        gap: 1.5, alignItems: "center",
                        transition: "background .15s",
                        "&:hover": { background: C.pageBg },
                      }}>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.muted }}>{idx + 1}</Typography>
                        <Typography sx={{ fontFamily: F.sans, fontSize: 14, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 13, color: C.ink }}>{numFmt(port.screened)}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 13, color: C.green, fontWeight: 700 }}>{numFmt(port.cleared)}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 13, color: C.red, fontWeight: 700 }}>{numFmt(port.high_risk)}</Typography>
                        <RiskBadge label={st} displayLabel={tRisk(st)} />
                      </Box>
                    );
                  })
              }

              {!loading && data?.ports && (
                <Box sx={{ display: "grid", gridTemplateColumns: "32px 1fr 100px 90px 100px 110px", px: 2.5, py: 1.6, background: C.tableTotalBg, borderTop: `2px solid ${C.cardBorder}`, gap: 1.5, alignItems: "center" }}>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.muted }}>∑</Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 13, fontWeight: 700, color: C.ink }}>TOTAL</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: C.ink }}>{numFmt(summary.total_screenings)}</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: C.green }}>{numFmt(summary.cleared)}</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: C.red }}>{numFmt(summary.high_risk)}</Typography>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.muted }}>—</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* ── CHARTS ROW ──────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr", gap: 2.5 }}>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title="Screenings Trend" sub="Last 7 days · auto-refreshed"
                right={trendDelta != null && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.4, borderRadius: "20px", background: trendDelta >= 0 ? C.greenLt : C.redLt }}>
                    {trendDelta >= 0 ? <TrendingUp sx={{ fontSize: 12, color: C.green }} /> : <TrendingDown sx={{ fontSize: 12, color: C.red }} />}
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: trendDelta >= 0 ? C.green : C.red, fontWeight: 700 }}>
                      {trendDelta >= 0 ? "+" : ""}{trendDelta}%
                    </Typography>
                  </Box>
                )}
              />
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trendData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.blue} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={C.blue} stopOpacity={0}    />
                    </linearGradient>
                    <linearGradient id="gRed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.red} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C.red} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.divider} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTip />} cursor={{ stroke: C.cardBorder }} />
                  <Area type="monotone" dataKey="screenings"       name="Total Screenings" stroke={C.blue} strokeWidth={2.5} fill="url(#gBlue)" dot={false} />
                  <Area type="monotone" dataKey="high_risk_alerts" name="High-Risk Alerts" stroke={C.red}  strokeWidth={2}   fill="url(#gRed)"  dot={false} strokeDasharray="4 2" />
                  <Legend wrapperStyle={{ fontFamily: F.mono, fontSize: 10, paddingTop: 8 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title="Outcome Distribution by Port of Entry" />
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                {[
                  { c: C.green, l: "Passed / Cleared" },
                  { c: C.red,   l: "High-Risk Alerts" },
                  { c: C.amber, l: "Referred for Follow-up" },
                ].map((item) => (
                  <Box key={item.l} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: item.c }} />
                    <Typography sx={{ fontFamily: F.mono, fontSize: 10, color: C.sub }}>{item.l}</Typography>
                  </Box>
                ))}
              </Box>
              <ResponsiveContainer width="100%" height={215}>
                <ReBarChart data={outcomeByPort} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="port" tick={{ fontFamily: F.mono, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} width={80} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: C.cardHover }} />
                  <Bar dataKey="cleared"   name="Cleared"   stackId="a" fill={C.green} />
                  <Bar dataKey="high_risk" name="High Risk" stackId="a" fill={C.red}   />
                  <Bar dataKey="follow_up" name="Follow-up" stackId="a" fill={C.amber} radius={[0, 4, 4, 0]} />
                </ReBarChart>
              </ResponsiveContainer>
              <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.muted, mt: 1, textAlign: "center" }}>Percentage of Total Screened</Typography>
            </Box>

            <Box sx={{ ...card(), p: 2.5, display: "flex", flexDirection: "column" }}>
              <SectionTitle title="Risk Level Summary" right={
                <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.muted }}>{numFmt(summary.total_screenings)} total</Typography>
              } />
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie data={riskChartData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={3} strokeWidth={0}>
                      {riskChartData.map((e: any) => <Cell key={e.name} fill={e.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none" }}>
                  <Typography sx={{ fontFamily: F.mono, fontSize: 24, fontWeight: 800, color: C.ink, lineHeight: 1 }}>
                    {numFmt(summary.total_screenings || 0)}
                  </Typography>
                  <Typography sx={{ fontFamily: F.sans, fontSize: 10, color: C.muted }}>Total</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "grid", gap: 1, mt: 1 }}>
                {[
                  { label: "Low Risk",      color: C.green },
                  { label: "High Risk",     color: C.red   },
                ].map((item) => {
                  const v  = riskChartData.find((r: any) => r.name === item.label)?.value ?? 0;
                  const pc = summary.total_screenings ? Math.round((v / summary.total_screenings) * 1000) / 10 : 0;
                  return (
                    <Box key={item.label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: item.color }} />
                        <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.ink }}>{item.label}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.sub }}>
                        {numFmt(v)} <span style={{ color: C.muted }}>({pc}%)</span>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* ── ANALYTICS ROW ───────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr", gap: 2.5 }}>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title="Country Analysis" />
              <Box sx={{ display: "grid", gap: 1.4 }}>
                {(countryData || []).map((item: any, idx: number) => {
                  const palette = [C.blue, C.teal, C.purple, C.amber, C.red, C.green];
                  const pct = summary.total_screenings ? Math.round((item.count / summary.total_screenings) * 100) : 0;
                  return (
                    <Box key={item.country || idx}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
                        <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.ink }}>{item.country || "Unknown"}</Typography>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.sub, fontWeight: 600 }}>{numFmt(item.count)}</Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, background: C.divider, overflow: "hidden" }}>
                        <Box sx={{ height: "100%", borderRadius: 3, background: palette[idx % palette.length], width: `${pct}%`, transition: "width 1s cubic-bezier(.4,0,.2,1)" }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ ...card(), p: 2.5 }}>
              <SectionTitle title="Symptoms Distribution" />
              {!(symptomData || []).some((s: any) => (s.count || 0) > 0) ? (
                <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.muted, py: 6, textAlign: "center" }}>
                  No symptoms reported in this period
                </Typography>
              ) : (
              <ResponsiveContainer width="100%" height={300}>
                <ReBarChart data={symptomData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gSymp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={C.blue} stopOpacity={1}   />
                      <stop offset="100%" stopColor={C.teal} stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.divider} vertical={false} />
                  <XAxis
                    dataKey="symptom"
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={80}
                    tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickCount={6}
                    domain={[0, "auto"]}
                    tickFormatter={(v) => String(Math.round(Number(v)))}
                    tick={{ fontFamily: F.mono, fontSize: 9, fill: C.muted }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name="Cases" fill="url(#gSymp)" radius={[5, 5, 0, 0]} />
                </ReBarChart>
              </ResponsiveContainer>
              )}
            </Box>

            <Box sx={{ ...card(), p: 2.5, display: "flex", flexDirection: "column" }}>
              <SectionTitle title="Overall Satisfaction" sub="WhatsApp travel-pass feedback" />
              {feedbackLoading ? (
                <Skeleton variant="rounded" height={190} />
              ) : !feedbackSummary.total ? (
                <Typography sx={{ fontFamily: F.sans, fontSize: 12, color: C.muted, py: 4, textAlign: "center" }}>
                  No feedback yet
                </Typography>
              ) : (
                <>
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 32, fontWeight: 800, color: satColor, lineHeight: 1, textShadow: glow(satColor) }}>
                      {satisfaction}%
                    </Typography>
                    <Typography sx={{ fontFamily: F.sans, fontSize: 11, fontWeight: 600, color: satColor, mt: 0.5 }}>
                      {satLabel}
                    </Typography>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 9, color: C.muted, mt: 0.3 }}>
                      Positive responses · {numFmt(feedbackSummary.total)} total
                    </Typography>
                  </Box>
                  <Box sx={{ height: 8, borderRadius: 4, background: C.divider, overflow: "hidden", mt: 0.5, mb: 1.2 }}>
                    <Box sx={{
                      height: "100%",
                      width: `${Math.min(100, Math.max(0, satisfaction))}%`,
                      background: `linear-gradient(90deg, ${satColor}, ${C.teal})`,
                      transition: "width .4s ease",
                    }} />
                  </Box>
                  <Box sx={{ display: "grid", gap: 0.8, mt: 0.5 }}>
                    {[
                      { emoji: "😄", label: "Positive", count: feedbackSummary.positive, color: C.green },
                      { emoji: "😐", label: "Neutral",  count: feedbackSummary.neutral,  color: C.amber },
                      { emoji: "😞", label: "Negative", count: feedbackSummary.negative, color: C.red },
                    ].map((row) => {
                      const pct = feedbackSummary.total
                        ? Math.round(((row.count || 0) / feedbackSummary.total) * 1000) / 10
                        : 0;
                      return (
                      <Box key={row.label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          <Typography component="span" sx={{ fontSize: 14, lineHeight: 1 }}>{row.emoji}</Typography>
                          <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.ink }}>{row.label}</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: F.mono, fontSize: 11, color: C.sub, fontWeight: 600 }}>
                          {numFmt(row.count)} <span style={{ color: C.muted }}>({pct}%)</span>
                        </Typography>
                      </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* ── FOLLOW-UP INSIGHTS ──────────────────────────────── */}
          {false && (
          <Box sx={{ ...card(), p: 2.5 }}>
            <SectionTitle title="21-Day Follow-up" sub="Sent · Responded · Yes / No among responses" />
            {followupLoading ? (
              <Skeleton variant="rounded" height={88} />
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1.5, mt: 0.5 }}>
                {[
                  { label: "Sent", value: followup?.sent ?? 0, color: C.blue },
                  { label: "Responded", value: followup?.responded ?? 0, color: C.teal },
                  { label: "Response rate", value: `${followup?.response_rate ?? 0}%`, color: C.amber },
                  { label: "Yes", value: followup?.yes ?? 0, color: C.green },
                  { label: "No", value: followup?.no ?? 0, color: C.red },
                ].map((cell) => (
                  <Box key={cell.label} sx={{
                    p: 1.6, borderRadius: "10px", border: `1px solid ${C.cardBorder}`,
                    background: C.pageBg, textAlign: "center",
                  }}>
                    <Typography sx={{ fontFamily: F.mono, fontSize: 22, fontWeight: 800, color: cell.color, lineHeight: 1.1 }}>
                      {typeof cell.value === "number" ? numFmt(cell.value) : cell.value}
                    </Typography>
                    <Typography sx={{ fontFamily: F.sans, fontSize: 11, color: C.sub, mt: 0.6, fontWeight: 600 }}>
                      {cell.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          )}

          {/* ── HIGH RISK TRAVEL CORRIDOR (from ui changes) ─────── */}
          <Box sx={{ ...card(), p: 2.5 }}>
            <SectionTitle title="High Risk Travel Corridor" sub="Origin country → entry point · Kenya time filters" />
            <HighRiskCorridorList
              corridors={corridorData?.corridors || []}
              totalHighRisk={corridorData?.total_high_risk || 0}
              loading={corridorLoading}
            />
          </Box>

          {/* ── SCREENING ACTIVITY HEATMAP (from ui changes) ────── */}
          <Box sx={{ ...card(), p: 2.5 }}>
            <SectionTitle title="Screening Activity Trend" sub="Screenings by day of week and hour (EAT)" />
            <ActivityHeatmapGrid
              days={activityHeatmapData?.days || []}
              hours={activityHeatmapData?.hours || []}
              matrix={activityHeatmapData?.matrix || []}
              rowTotals={activityHeatmapData?.row_totals || []}
              colTotals={activityHeatmapData?.col_totals || []}
              grandTotal={activityHeatmapData?.grand_total || 0}
              loading={activityHeatmapLoading}
            />
          </Box>

          {error && (
            <Box sx={{ ...card(), p: 2.5, borderLeft: `4px solid ${C.red}`, background: C.redLt }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ErrorOutline sx={{ fontSize: 18, color: C.red }} />
                <Typography sx={{ fontFamily: F.mono, fontSize: 12, color: C.red }}>{error}</Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ height: 20 }} />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
